import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Alert, Button, Heading, Paragraph, Skeleton, Table } from '@digdir/designsystemet-react';
import { Routes } from '../../../enums/Routes';
import { buildRoute } from '../../../utils/routeBuilder';
import {
  formatScore,
  getScoreLevel,
  levelTones,
  MAX_SCORE,
} from '../../../components/AnalysisLayout/analysis.utils';
import { trendTones, type QuestionRow } from '../dashboard.utils';
import classes from '../TeamDashboard.module.css';

const arrows = { up: '↗', down: '↘', stable: '—' } as const;

type QuestionTableProps = {
  teamId: number;
  rows: QuestionRow[];
  isLoading: boolean;
  error: Error | null;
};

/** Hvert spørsmål med snittet i perioden, siste måling og utviklingen */
export const QuestionTable = ({ teamId, rows, isLoading, error }: QuestionTableProps) => {
  const { t } = useTranslation();

  const content = () => {
    if (isLoading) {
      return (
        <div aria-busy="true" className={classes.tablePadding}>
          <Skeleton variant="rectangle" width="100%" height={280} />
        </div>
      );
    }
    if (error) {
      return (
        <Alert data-color="danger">
          <Paragraph>{error.message}</Paragraph>
        </Alert>
      );
    }
    if (rows.length === 0) {
      return (
        <div className={classes.emptyCard}>
          <Heading level={3} data-size="2xs">
            {t('dashboard.questionScores.noData')}
          </Heading>
          <Paragraph data-size="sm" className={classes.subtle}>
            {t('dashboard.questionScores.noDataDescription')}
          </Paragraph>
        </div>
      );
    }
    return (
      <Table className={classes.table}>
        <Table.Head>
          <Table.Row>
            <Table.HeaderCell scope="col">{t('dashboard.questions.question')}</Table.HeaderCell>
            <Table.HeaderCell scope="col" className={classes.colAverage}>
              {t('dashboard.questions.average')}
            </Table.HeaderCell>
            <Table.HeaderCell scope="col" className={classes.colLatest}>
              {t('dashboard.questions.latest')}
            </Table.HeaderCell>
            <Table.HeaderCell scope="col" className={classes.colTrend}>
              {t('dashboard.questions.trend')}
            </Table.HeaderCell>
          </Table.Row>
        </Table.Head>
        <Table.Body>
          {rows.map((row) => (
            <Table.Row key={row.key}>
              <Table.Cell>{row.question}</Table.Cell>
              <Table.Cell>
                <span className={classes.scoreCell}>
                  <span className={classes.cellNumber}>{formatScore(row.average, 2)}</span>
                  <span aria-hidden="true" className={`${classes.track} ${classes.smallTrack}`}>
                    <span
                      className={classes.fill}
                      data-tone={levelTones[getScoreLevel(row.average)]}
                      style={{ width: `${Math.min(100, (row.average / MAX_SCORE) * 100)}%` }}
                    />
                  </span>
                </span>
              </Table.Cell>
              <Table.Cell>
                <span className={classes.cellNumber}>
                  {row.latest !== undefined ? formatScore(row.latest, 2) : '–'}
                </span>
              </Table.Cell>
              <Table.Cell>
                {row.direction ? (
                  <span className={classes.trendText} data-tone={trendTones[row.direction]}>
                    <span aria-hidden="true">{arrows[row.direction]} </span>
                    {t(`dashboard.trend.direction.${row.direction}`)}
                  </span>
                ) : (
                  '–'
                )}
              </Table.Cell>
            </Table.Row>
          ))}
        </Table.Body>
      </Table>
    );
  };

  return (
    <section aria-labelledby="questions-heading" className={classes.section}>
      <div className={classes.sectionHeader}>
        <Heading id="questions-heading" level={2} data-size="md" tabIndex={-1}>
          {t('dashboard.questions.title')}
        </Heading>
        <Button asChild variant="secondary" data-size="sm">
          <Link to={buildRoute(Routes.QuestionTrends, { teamId })}>
            {t('dashboard.questionTrends.seeDetailed')}
          </Link>
        </Button>
      </div>
      <div className={classes.tableCard}>{content()}</div>
    </section>
  );
};
